const PDF_JS_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const PDF_WORKER_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
const ZOOM_LEVELS = [1, 1.25, 1.5, 2];

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      resolve();
    });
    script.addEventListener('error', reject);
    document.head.appendChild(script);
  });
}

class BookPdfModal {
  static triggersBound = false;
  static instances = new Map();

  constructor(dialog) {
    this.dialog = dialog;
    this.pdfDoc = null;
    this.pdfUrl = null;
    this.opener = null;
    this.zoomIndex = 0;
    this.isExpanded = false;
    this.renderedPages = new Map();
    this.observer = null;
    this.pdfjsLib = null;
    this.onResize = this.debounce(() => {
      if (!this.dialog.open) return;
      this.refreshVisiblePages();
    }, 200);

    this.closeBtn = dialog.querySelector('[data-action="close"]');
    this.zoomInBtn = dialog.querySelector('[data-action="zoom-in"]');
    this.zoomOutBtn = dialog.querySelector('[data-action="zoom-out"]');
    this.expandBtn = dialog.querySelector('[data-action="expand"]');
    this.zoomLabel = dialog.querySelector('[data-zoom-label]');
    this.statusEl = dialog.querySelector('[data-status]');
    this.viewport = dialog.querySelector('[data-viewport]');
    this.pagesContainer = dialog.querySelector('[data-pages]');

    this.closeBtn?.addEventListener('click', () => this.close());
    this.zoomInBtn?.addEventListener('click', () => this.setZoom(this.zoomIndex + 1));
    this.zoomOutBtn?.addEventListener('click', () => this.setZoom(this.zoomIndex - 1));
    this.expandBtn?.addEventListener('click', () => this.toggleExpand());

    this.dialog.addEventListener('click', (event) => {
      const panel = this.dialog.querySelector('[data-panel]');
      if (panel && !panel.contains(event.target)) this.close();
    });

    this.dialog.addEventListener('close', () => this.onDialogClose());

    window.addEventListener('resize', this.onResize);
    BookPdfModal.instances.set(dialog.id, this);
  }

  static initAll() {
    document.querySelectorAll('dialog.book-pdf-modal').forEach((dialog) => {
      if (!BookPdfModal.instances.has(dialog.id)) {
        new BookPdfModal(dialog);
      }
    });

    if (!BookPdfModal.triggersBound) {
      document.addEventListener('click', BookPdfModal.handleTriggerClick);
      BookPdfModal.triggersBound = true;
    }
  }

  static handleTriggerClick(event) {
    const trigger = event.target.closest('[data-book-pdf-trigger]');
    if (!trigger) return;

    const modal = BookPdfModal.instances.get(trigger.dataset.modalTarget);
    if (!modal) return;

    event.preventDefault();
    modal.open(trigger.dataset.pdfUrl, trigger);
  }

  debounce(fn, delay) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  async ensurePdfJs() {
    if (this.pdfjsLib) return this.pdfjsLib;

    await loadScript(PDF_JS_URL);
    this.pdfjsLib = window.pdfjsLib;

    if (!this.pdfjsLib) {
      throw new Error('PDF.js failed to load');
    }

    this.pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_URL;
    return this.pdfjsLib;
  }

  async open(url, opener) {
    this.pdfUrl = url;
    this.opener = opener;
    this.zoomIndex = 0;
    this.isExpanded = false;
    this.dialog.classList.remove('book-pdf-modal--expanded');
    this.expandBtn?.setAttribute('aria-pressed', 'false');
    this.expandBtn?.setAttribute('aria-label', 'Expand viewer');

    this.statusEl.hidden = false;
    this.statusEl.textContent = 'Loading sample…';
    this.pagesContainer.innerHTML = '';
    this.renderedPages.clear();
    this.observer?.disconnect();

    this.updateZoomUI();
    this.dialog.showModal();
    this.dialog.querySelector('[data-panel]')?.focus({ focusVisible: false });

    try {
      await this.ensurePdfJs();
      const loadingTask = this.pdfjsLib.getDocument({
        url,
        cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/',
        cMapPacked: true,
      });
      this.pdfDoc = await loadingTask.promise;
      this.buildPagePlaceholders();
      this.setupObserver();
      this.statusEl.hidden = true;
    } catch (error) {
      console.error('Book PDF viewer failed:', error);
      window.open(url, '_blank', 'noopener,noreferrer');
      this.close();
    }
  }

  close() {
    if (this.dialog.open) {
      this.dialog.close();
    } else {
      this.onDialogClose();
    }
  }

  onDialogClose() {
    this.dialog.classList.remove('book-pdf-modal--expanded');
    this.isExpanded = false;
    this.observer?.disconnect();
    this.observer = null;
    this.renderedPages.clear();
    this.pagesContainer.innerHTML = '';

    if (this.pdfDoc) {
      this.pdfDoc.destroy();
      this.pdfDoc = null;
    }

    if (this.opener) {
      this.opener.focus();
      this.opener = null;
    }
  }

  buildPagePlaceholders() {
    const pageCount = this.pdfDoc.numPages;

    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
      const pageElement = document.createElement('div');
      pageElement.className = 'book-pdf-modal__page';
      pageElement.dataset.pageNumber = String(pageNumber);
      pageElement.setAttribute('aria-label', `Page ${pageNumber}`);

      const canvas = document.createElement('canvas');
      pageElement.appendChild(canvas);
      this.pagesContainer.appendChild(pageElement);
    }
  }

  setupObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const pageNumber = parseInt(entry.target.dataset.pageNumber, 10);
          this.renderPage(pageNumber, entry.target);
        });
      },
      { root: this.viewport, rootMargin: '300px 0px' }
    );

    this.pagesContainer.querySelectorAll('.book-pdf-modal__page').forEach((pageElement) => {
      this.observer.observe(pageElement);
    });
  }

  getContainerWidth() {
    const styles = window.getComputedStyle(this.pagesContainer);
    const paddingLeft = parseFloat(styles.paddingLeft) || 0;
    const paddingRight = parseFloat(styles.paddingRight) || 0;
    return Math.max(this.viewport.clientWidth - paddingLeft - paddingRight, 280);
  }

  getZoomScale() {
    return ZOOM_LEVELS[this.zoomIndex];
  }

  async renderPage(pageNumber, pageElement, force = false) {
    const renderKey = `${pageNumber}-${this.zoomIndex}`;
    if (!force && this.renderedPages.get(pageNumber) === renderKey) return;

    const canvas = pageElement.querySelector('canvas');
    const page = await this.pdfDoc.getPage(pageNumber);
    const baseViewport = page.getViewport({ scale: 1 });
    const containerWidth = this.getContainerWidth();
    const outputScale = window.devicePixelRatio || 1;
    const scale = (containerWidth / baseViewport.width) * this.getZoomScale() * outputScale;
    const viewport = page.getViewport({ scale });

    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvas.style.width = `${viewport.width / outputScale}px`;
    canvas.style.height = `${viewport.height / outputScale}px`;

    await page.render({
      canvasContext: canvas.getContext('2d'),
      viewport,
    }).promise;

    this.renderedPages.set(pageNumber, renderKey);
  }

  isPageNearViewport(pageElement) {
    const rect = pageElement.getBoundingClientRect();
    const viewportRect = this.viewport.getBoundingClientRect();
    return rect.bottom > viewportRect.top - 300 && rect.top < viewportRect.bottom + 300;
  }

  async refreshVisiblePages() {
    if (!this.pdfDoc) return;

    this.renderedPages.clear();
    const pageElements = this.pagesContainer.querySelectorAll('.book-pdf-modal__page');

    for (const pageElement of pageElements) {
      if (this.isPageNearViewport(pageElement)) {
        const pageNumber = parseInt(pageElement.dataset.pageNumber, 10);
        await this.renderPage(pageNumber, pageElement, true);
      }
    }
  }

  async setZoom(nextIndex) {
    if (nextIndex < 0 || nextIndex >= ZOOM_LEVELS.length) return;

    this.zoomIndex = nextIndex;
    this.updateZoomUI();
    await this.refreshVisiblePages();
  }

  updateZoomUI() {
    const percent = Math.round(this.getZoomScale() * 100);
    if (this.zoomLabel) this.zoomLabel.textContent = `${percent}%`;
    if (this.zoomOutBtn) this.zoomOutBtn.disabled = this.zoomIndex === 0;
    if (this.zoomInBtn) this.zoomInBtn.disabled = this.zoomIndex === ZOOM_LEVELS.length - 1;
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
    this.dialog.classList.toggle('book-pdf-modal--expanded', this.isExpanded);
    this.expandBtn?.setAttribute('aria-pressed', String(this.isExpanded));
    this.expandBtn?.setAttribute('aria-label', this.isExpanded ? 'Exit expanded view' : 'Expand viewer');
    requestAnimationFrame(() => this.refreshVisiblePages());
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => BookPdfModal.initAll());
} else {
  BookPdfModal.initAll();
}
